'use client';
import Script from 'next/script';
import {
  useRef,
  forwardRef,
  useImperativeHandle,
  useEffect,
  useState,
  useCallback,
} from 'react';

interface HubSpotEmbedFormProps {
  portalId: string;
  formId: string;
  onFormReady?: () => void;
}

export interface HubspotFormRef {
  submitHubspotForm: (
    data: Array<
      { name: string; value: string } | { name: string; value: File }
    >,
  ) => Promise<boolean>;
}

const SUBMIT_TIMEOUT_MS = 8000;

const HubSpotEmbedForm = forwardRef<HubspotFormRef, HubSpotEmbedFormProps>(
  ({ portalId, formId, onFormReady }: HubSpotEmbedFormProps, ref) => {
    const formContainerRef = useRef<HTMLDivElement>(null);
    const [formSubmissionCount, setFormSubmissionCount] = useState(0);
    const pendingResolveRef = useRef<((success: boolean) => void) | null>(null);
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const reloadFormRef = useRef(() => {
      setFormSubmissionCount((prev) => prev + 1);
    });

    const loadForm = useCallback(() => {
      if (window.hbspt) {
        window.hbspt.forms.create({
          portalId,
          formId,
          target: '#hubspotForm-' + formId,
          region: 'na1',
          onFormSubmitted: () => {
            if (pendingResolveRef.current) {
              if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
              }
              timeoutRef.current = null;
              pendingResolveRef.current(true);
              pendingResolveRef.current = null;
            }
            setFormSubmissionCount((prev) => prev + 1);
          },
          onFormReady,
        });
      }
    }, [portalId, formId, onFormReady]);

    useEffect(() => {
      loadForm();
    }, [formSubmissionCount, loadForm]);

    useImperativeHandle(
      ref,
      () => ({
        submitHubspotForm: (data) => {
          return new Promise<boolean>((resolve) => {
            try {
              if (!formContainerRef.current) {
                resolve(false);
                return;
              }

              const iframe = formContainerRef.current.querySelector('iframe');
              if (!iframe) {
                resolve(false);
                return;
              }

              const iframeWindow = iframe.contentWindow;
              if (!iframeWindow) {
                resolve(false);
                return;
              }

              const iframeDocument = iframeWindow.document;

              const inputs = iframeDocument.querySelectorAll<HTMLInputElement>(
                'input, select, textarea',
              );

              inputs.forEach((input: HTMLInputElement) => {
                const fieldName = input.name;
                const dataElement = data.find(
                  (e) => fieldName === e.name,
                )?.value;
                if (dataElement instanceof File) {
                  const dataTransfer = new DataTransfer();
                  dataTransfer.items.add(dataElement);
                  input.files = dataTransfer.files;
                  input.dispatchEvent(new Event('input', { bubbles: true }));
                  input.dispatchEvent(new Event('change', { bubbles: true }));
                } else if (typeof dataElement === 'string') {
                  input.value = dataElement;
                  input.dispatchEvent(new Event('input', { bubbles: true }));
                  input.dispatchEvent(new Event('change', { bubbles: true }));
                }
              });

              const submitButton = iframeDocument.querySelector(
                'input[type="submit"]',
              ) as HTMLInputElement;
              if (!submitButton) {
                resolve(false);
                return;
              }

              pendingResolveRef.current = resolve;
              timeoutRef.current = setTimeout(() => {
                if (pendingResolveRef.current) {
                  pendingResolveRef.current = null;
                  reloadFormRef.current();
                  resolve(false);
                }
                timeoutRef.current = null;
              }, SUBMIT_TIMEOUT_MS);

              submitButton.click();
            } catch (err) {
              console.error('Error submitting form:', err);
              resolve(false);
            }
          });
        },
      }),
      [],
    );

    return (
      <>
        <div
          id={'hubspotForm-' + formId}
          ref={formContainerRef}
          className="hidden"
        />
        <Script
          src="https://js.hsforms.net/forms/embed/v2.js"
          strategy="lazyOnload"
          onLoad={loadForm}
        />
      </>
    );
  },
);

HubSpotEmbedForm.displayName = 'HubSpotEmbedForm';

export default HubSpotEmbedForm;
